<template>
  <div class="site-pass-time-form">
    <div class="site-pass-time-form__header">
      <Button
        icon="far fa-arrow-left"
        class="p-button-text p-button-rounded"
        @click="$emit('cancel')"
      />
      <div class="site-pass-time-form__title">
        <h2>{{ t('flows.operators.sitePassTime.label') }}</h2>
        <span class="site-pass-time-form__subtitle">
          {{ t('flows.operatorKinds.trigger') }}
        </span>
      </div>
      <Button
        icon="far fa-times"
        class="p-button-text p-button-rounded"
        @click="$emit('close')"
      />
    </div>

    <div class="site-pass-time-form__content">
      <div class="site-pass-time-form__field">
        <label class="site-pass-time-form__label">
          {{ t('flows.operators.sitePassTime.timeElapsed') }}
        </label>
        <div class="site-pass-time-form__time-selector">
          <InputNumber
            v-model="localData.time_unit_select.value"
            :min="1"
            :max="999"
            class="site-pass-time-form__input"
          />
          <Dropdown
            v-model="localData.time_unit_select.unit"
            :options="timeUnits"
            option-label="label"
            option-value="value"
            class="site-pass-time-form__dropdown"
          />
        </div>
        <small
          v-if="formValidation?.time_unit_select?.$errors?.length"
          class="p-error"
        >
          {{ formValidation.time_unit_select.$errors[0].$message }}
        </small>
      </div>

      <div class="site-pass-time-form__field">
        <label class="site-pass-time-form__label">
          {{ t('flows.operators.sitePassTime.period') }}
        </label>
        <div class="site-pass-time-form__options">
          <div
            v-for="option in sessionOptions"
            :key="option.value"
            class="site-pass-time-form__option"
            :class="{ 'site-pass-time-form__option--selected': localData.multiplebox_global_session === option.value }"
            @click="localData.multiplebox_global_session = option.value"
          >
            <i :class="option.icon" />
            <div>
              <span class="site-pass-time-form__option-label">{{ option.label }}</span>
              <span class="site-pass-time-form__option-desc">{{ option.description }}</span>
            </div>
          </div>
        </div>
        <small
          v-if="formValidation?.multiplebox_global_session?.$errors?.length"
          class="p-error"
        >
          {{ formValidation.multiplebox_global_session.$errors[0].$message }}
        </small>
      </div>
    </div>

    <div class="site-pass-time-form__footer">
      <Button
        :label="t('flows.operatorPanel.cancel')"
        class="p-button-text"
        @click="$emit('cancel')"
      />
      <Button
        :label="t('flows.operatorPanel.addStep')"
        class="p-button-success"
        @click="$emit('submit')"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import type { SitePassTimeData } from './index';

export default defineComponent({
  name: 'SitePassTimeForm',

  components: {
    Button,
    InputNumber,
    Dropdown,
  },

  props: {
    modelValue: {
      type: Object as PropType<SitePassTimeData>,
      default: () => ({
        time_unit_select: { unit: 'MINUTE', value: 10 },
        multiplebox_global_session: 'global',
      }),
    },
    formValidation: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue', 'cancel', 'close', 'submit'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const localData = ref<SitePassTimeData>({
      time_unit_select: { ...props.modelValue.time_unit_select },
      multiplebox_global_session: props.modelValue.multiplebox_global_session,
    });

    const timeUnits = [
      { value: 'SECOND', label: t('flows.operators.sitePassTime.seconds') },
      { value: 'MINUTE', label: t('flows.operators.sitePassTime.minutes') },
      { value: 'HOUR', label: t('flows.operators.sitePassTime.hours') },
    ];

    const sessionOptions = [
      {
        value: 'global',
        label: t('flows.operators.sitePassTime.global'),
        description: t('flows.operators.sitePassTime.globalDesc'),
        icon: 'far fa-globe',
      },
      {
        value: 'session',
        label: t('flows.operators.sitePassTime.session'),
        description: t('flows.operators.sitePassTime.sessionDesc'),
        icon: 'far fa-window',
      },
    ];

    watch(localData, (newData) => {
      emit('update:modelValue', newData);
    }, { deep: true });

    watch(() => props.modelValue, (newValue) => {
      if (newValue !== localData.value) {
        localData.value = {
          time_unit_select: { ...newValue.time_unit_select },
          multiplebox_global_session: newValue.multiplebox_global_session,
        };
      }
    });

    return {
      t,
      localData,
      timeUnits,
      sessionOptions,
    };
  },
});
</script>

<style lang="scss" scoped>
.site-pass-time-form {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;

    .p-button {
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
    }
  }

  &__title {
    flex: 1;

    h2 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #1f2937;
    }
  }

  &__subtitle {
    font-size: 0.75rem;
    color: #6b7280;
  }

  &__content {
    flex: 1;
    padding: 1rem;
    overflow-y: auto;
  }

  &__field {
    margin-bottom: 1.5rem;
  }

  &__label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  &__time-selector {
    display: flex;
    gap: 0.5rem;
  }

  &__input {
    width: 100px;
  }

  &__dropdown {
    flex: 1;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__option {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
      font-size: 1rem;
      color: #6b7280;
      margin-top: 0.125rem;
    }

    &:hover {
      border-color: #3b82f6;
      background: #f0f9ff;
    }

    &--selected {
      border-color: #3b82f6;
      background: #eff6ff;

      i {
        color: #3b82f6;
      }
    }
  }

  &__option-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  &__option-desc {
    display: block;
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.125rem;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem;
    border-top: 1px solid #e5e7eb;
  }
}
</style>
